import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { Image } from 'primeng/image';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import SampleBranding from '../../../../../types/sample-vehicle-model/SampleBranding';
import SampleVehicleModel from '../../../../../types/sample-vehicle-model/SampleVehicleModel';
import Sample from '../../../../../types/sample/Sample';
import VehicleModel from '../../../../../types/vehicle-model/VehicleModel';
import VehicleModelWriteDTO from '../../../../../types/vehicle-model/VehicleModelWriteDTO';
import { IsAdminDirective } from '../../../directives/is-admin.directive';
import { IsMaintainerDirective } from '../../../directives/is-maintainer.directive';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../../pipes/format-date-time';
import { FormatDatePipe } from '../../../pipes/format-date.pipe';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { FieldOfUseSeverityPipe } from '../../../pipes/sample-field-of-use-severity.pipe';
import { SampleStateSeverityPipe } from '../../../pipes/sample-state-severity.pipe';
import { VehicleModelService } from '../../../services/vehicle-model.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import {
    ChangeSampleVehicleModelAvailabilityDialogComponent
} from '../../shared/samples/change-sample-vehicle-model-availability-dialog/change-sample-vehicle-model-availability-dialog.component';
import {
    AddSamplesToVehicleModelDialogComponent
} from '../../shared/vehicle-models/add-samples-to-vehicle-model-dialog/add-samples-to-vehicle-model-dialog.component';
import {
    ChangeSalesCodesDialogComponent
} from '../../shared/vehicle-models/change-sales-codes-dialog/change-sales-codes-dialog.component';
import {
    UpdateVehicleModelDialogComponent
} from '../../shared/vehicle-models/update-vehicle-model-dialog/update-vehicle-model-dialog.component';

@Component({
    selector: 'app-vehicle-model-detail',
    templateUrl: './vehicle-model-detail.component.html',
    styleUrls: [ './vehicle-model-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateVehicleModelDialogComponent,
        SharedModule,
        UpdateVehicleModelDialogComponent,
        TableModule,
        AddSamplesToVehicleModelDialogComponent,
        Tag,
        FieldOfUseSeverityPipe,
        SampleStateSeverityPipe,
        EnumLabelPipe,
        Image,
        FormatDateTime,
        FormsModule,
        FormatDatePipe,
        IsMaintainerDirective,
        IsAdminDirective,
        ChangeSalesCodesDialogComponent,
        ChangeSampleVehicleModelAvailabilityDialogComponent,
        Tooltip,
        RoleNamePipe,
        InputText,
        IconField,
        InputIcon,
        Select
    ]
})
export class VehicleModelDetailComponent implements OnInit {
    public vehicleModel: VehicleModel | null = null;
    public editableVehicleModel: VehicleModelWriteDTO = new VehicleModelWriteDTO({});
    public selectedSample: Sample | null = null;
    public displayUpdateDialog: boolean = false;
    public displayAddSampleDialog: boolean = false;
    public displayChangeVehicleModelAvailabilityDialog = false;
    public displayChangeSalesCodesDialog = false;
    public brandingOptions = [
        { label: 'Standard', value: SampleBranding.STANDARD },
        { label: 'Manufaktur', value: SampleBranding.MANUFAKTUR },
        { label: 'Manufaktur Exclusive', value: SampleBranding.MANUFAKTUR_EXCLUSIVE }
    ];

    @ViewChild('samplesTable') samplesTable!: Table;

    constructor(
        private route: ActivatedRoute,
        private vehicleModelService: VehicleModelService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    ngOnInit(): void {
        this.loadVehicleModel();
    }

    private loadVehicleModel(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            this.vehicleModelService
                .getVehicleModel(idNumber)
                .then((result: VehicleModel) => {
                    this.vehicleModel = result;
                    this.vehicleModel.samples.forEach(sample => {
                        if (sample.sample) {
                            sample.sample.detailNames = this.getDetailNames(sample.sample);
                        }
                        if (Array.isArray(sample.salesCodes)) {
                            sample.salesCodes = [ ...sample.salesCodes ].sort((a, b) => a.localeCompare(b));
                        }
                    });
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
        }
    }

    openEditDialog(): void {
        this.editableVehicleModel = new VehicleModelWriteDTO(this.vehicleModel ?? {});
        this.editableVehicleModel.brandId = this.vehicleModel?.brand?.id ?? undefined;
        this.editableVehicleModel.bodyTypeId = this.vehicleModel?.bodyType?.id ?? undefined;
        this.editableVehicleModel.carClassId = this.vehicleModel?.carClass?.id ?? undefined;
        this.displayUpdateDialog = true;
    }

    reloadVehicleModelDetails(): void {
        this.loadVehicleModel();
    }

    confirmDelete(): void {
        if (!this.vehicleModel) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the vehicle model '${ this.vehicleModel.modelSeries }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    if (!this.vehicleModel) {
                        return;
                    }
                    await this.vehicleModelService.deleteVehicleModel(this.vehicleModel.id);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Vehicle model deleted successfully',
                        life: 3000,
                    });
                    // Navigate back to the vehicle models page
                    window.history.back();
                } catch (error: any) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                }
            },
        });
    }

    public openChangeVehicleModelAvailabilityDialog(sample: SampleVehicleModel): void {
        if (!this.vehicleModel || !sample) {
            return;
        }

        this.vehicleModel.sampleAvailabilityDate = sample.availabilityDate;
        this.vehicleModel.sampleExpirationDate = sample.expirationDate;
        this.selectedSample = sample.sample!;
        this.displayChangeVehicleModelAvailabilityDialog = true;
    }

    public openChangeSalesCodesDialog(sample: SampleVehicleModel): void {
        if (!this.vehicleModel || !sample) {
            return;
        }

        this.vehicleModel.salesCodes = sample.salesCodes;
        this.selectedSample = sample.sample!;
        this.displayChangeSalesCodesDialog = true;
    }

    public removeSample(sample: Sample | undefined) {
        if (!sample || !this.vehicleModel) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to remove the sample '${ sample.printedName }' from the vehicle model '${ this.vehicleModel.modelSeries }'?`,
            header: 'Confirm Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    if (!this.vehicleModel) {
                        return;
                    }
                    await this.vehicleModelService.removeSampleFromVehicleModel(this.vehicleModel.id, sample.id);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Removed',
                        detail: 'Sample removed from vehicle model successfully',
                        life: 3000,
                    });
                    this.loadVehicleModel();
                } catch (error: any) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                }
            },
        });
    }

    public onChangeBranding(newBranding: SampleBranding, sample: SampleVehicleModel) {
        const previousBranding = sample.sampleBranding;

        this.vehicleModelService.changeSampleBranding(this.vehicleModel?.id || 0, sample?.sample?.id || 0, newBranding)
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Vehicle model updated successfully.',
                    life: 3000
                });
            }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });

            sample.sampleBranding = previousBranding;
        });
    }

    public openAddSampleDialog() {
        this.displayAddSampleDialog = true;
    }

    public getDetailNames(sample: Sample): string {
        return sample.categoryDetails.map(detail => detail.name).join(', ');
    }

    public onGlobalSearch(event: Event): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        this.samplesTable.filterGlobal(value, 'contains');
    }
}
